import { Airfare, Segment } from '@msc-b2c/interfaces/types';
import {
  CabinSelectionsType,
  formatPrice,
  getFormatPriceCurrencyOrder,
  roundPrice,
} from '@msc-b2c/shared-utils';
import { Item, IFlightClassConfig } from '@msc-b2c/interfaces/interfaces';
import { CabinPreferenceEnum, ClassCodeFlightEnum } from '@msc-b2c/interfaces/enums';
import { formatCity } from './flightRecapUtils';
import { getFlightClassLabel } from './flightClassUtils';

export const DEFAULT_FLIGHT_CLASS_ORDER: ClassCodeFlightEnum[] = [
  ClassCodeFlightEnum.ECONOMY,
  ClassCodeFlightEnum.PREMIUM_CLASS,
  ClassCodeFlightEnum.BUSINESS_CLASS,
  ClassCodeFlightEnum.FIRST_CLASS,
];

type HiddenFlightClassConfig = Partial<Record<ClassCodeFlightEnum, boolean>>;

const isFlightClassCode = (code?: string): code is ClassCodeFlightEnum =>
  Object.values(ClassCodeFlightEnum).includes(code as ClassCodeFlightEnum);

export const getOrderedFlightClassCodes = (
  flightClasses: IFlightClassConfig[] = [],
  hiddenClasses: HiddenFlightClassConfig = {}
): ClassCodeFlightEnum[] => {
  const configuredCodes = flightClasses
    .map((flightClass) => flightClass.code)
    .filter(isFlightClassCode);

  const orderedCodes = configuredCodes.length > 0 ? configuredCodes : DEFAULT_FLIGHT_CLASS_ORDER;

  return Array.from(new Set(orderedCodes)).filter((classCode) => !hiddenClasses[classCode]);
};

const getAirfareTotal = (airfare?: Airfare | null): number => {
  const total = Number(airfare?.price?.total);
  return Number.isFinite(total) ? total : 0;
};

export const getMatchingRouteUpgradeAirfare = (
  airfares: Airfare[],
  selectedAirfare: Airfare | null,
  orderedClassCodes: ClassCodeFlightEnum[]
): Airfare | null => {
  if (!selectedAirfare) return null;

  const selectedClassIndex = orderedClassCodes.indexOf(selectedAirfare.price.classCode);
  if (selectedClassIndex < 0) return null;

  const selectedTotal = getAirfareTotal(selectedAirfare);
  const higherClassCodes = orderedClassCodes.slice(selectedClassIndex + 1);

  const matchingUpgradeAirfares = airfares.filter((airfare) => {
    const outboundOrigin = airfare.outbound.segments[0]?.origin.airport.code;
    const outboundDestination =
      airfare.outbound.segments[airfare.outbound.segments.length - 1]?.destination.airport.code;
    const returnOrigin = airfare.return?.segments[0]?.origin.airport.code || '';
    const returnDestination =
      airfare.return?.segments[airfare.return.segments.length - 1]?.destination.airport.code || '';
    const selectedOutboundOrigin = selectedAirfare.outbound.segments[0]?.origin.airport.code;
    const selectedOutboundDestination =
      selectedAirfare.outbound.segments[selectedAirfare.outbound.segments.length - 1]?.destination
        .airport.code;
    const selectedReturnOrigin = selectedAirfare.return?.segments[0]?.origin.airport.code || '';
    const selectedReturnDestination =
      selectedAirfare.return?.segments[selectedAirfare.return.segments.length - 1]?.destination
        .airport.code || '';

    const isMatchingRoute =
      outboundOrigin === selectedOutboundOrigin &&
      outboundDestination === selectedOutboundDestination &&
      returnOrigin === selectedReturnOrigin &&
      returnDestination === selectedReturnDestination;

    return (
      isMatchingRoute &&
      higherClassCodes.includes(airfare.price.classCode) &&
      getAirfareTotal(airfare) > selectedTotal
    );
  });

  return (
    matchingUpgradeAirfares.sort(
      (firstAirfare, secondAirfare) =>
        orderedClassCodes.indexOf(firstAirfare.price.classCode) -
          orderedClassCodes.indexOf(secondAirfare.price.classCode) ||
        getAirfareTotal(firstAirfare) - getAirfareTotal(secondAirfare)
    )[0] ?? null
  );
};

const isRawGdsClassDescription = (description: string): boolean =>
  description.includes('/') || /\bCLASS\b/i.test(description);

const toDisplayClassLabel = (text: string): string => {
  const trimmed = text.trim();
  if (!trimmed) return '';

  if (trimmed === trimmed.toUpperCase()) {
    return trimmed
      .toLowerCase()
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  return trimmed;
};

const normalizeCabinClassDescription = (description: string): string => {
  let normalized = description.trim();

  if (normalized.includes('/')) {
    normalized = normalized.split('/').pop()?.trim() ?? normalized;
  }

  normalized = normalized.replace(/\s+CLASS\s*$/i, '').trim();

  return toDisplayClassLabel(normalized);
};

export const getSegmentFlightClassLabel = (
  segment?: Segment,
  legClassCode?: ClassCodeFlightEnum,
  flightClasses: IFlightClassConfig[] = []
): string => {
  const cabinClassDescription = segment?.cabinClassDescription?.trim();
  const configuredLabel = getFlightClassLabel(legClassCode, flightClasses);

  if (cabinClassDescription) {
    if (isRawGdsClassDescription(cabinClassDescription)) {
      return configuredLabel || normalizeCabinClassDescription(cabinClassDescription);
    }

    return cabinClassDescription;
  }

  return configuredLabel;
};

export const getSelectedFlightClassLabel = (
  selectedAirfare?: Airfare | null,
  flightClasses: IFlightClassConfig[] = []
): string => {
  if (!selectedAirfare) return '';

  const classCode = selectedAirfare.outbound?.classCode ?? selectedAirfare.price?.classCode;
  const configuredLabel =
    flightClasses.find((flightClass) => flightClass.code === classCode)?.label?.trim() ||
    getFlightClassLabel(classCode, flightClasses);

  if (configuredLabel) {
    return configuredLabel;
  }

  return getSegmentFlightClassLabel(selectedAirfare.outbound?.segments?.[0], classCode);
};

export const buildFlightSummarySidebarLabel = ({
  flightLabel,
  selectedAirfare,
  flightClasses = [],
  trainLabel,
  hasTrains = false,
}: {
  flightLabel?: string;
  selectedAirfare?: Airfare | null;
  flightClasses?: IFlightClassConfig[];
  trainLabel?: string;
  hasTrains?: boolean;
}): string => {
  if (!selectedAirfare) {
    return flightLabel ?? '';
  }

  const outbound = selectedAirfare.outbound?.segments?.[0];
  const lastOutboundSegment =
    selectedAirfare.outbound?.segments?.[selectedAirfare.outbound.segments.length - 1];
  const origin = formatCity(outbound?.origin?.airport?.name);
  const destination = formatCity(lastOutboundSegment?.destination?.airport?.name);
  const classLabel = getSelectedFlightClassLabel(selectedAirfare, flightClasses);

  const routeLabel = origin && destination ? `${origin} - ${destination}` : '';
  let summary = [flightLabel?.trim(), routeLabel].filter(Boolean).join(' ');

  if (classLabel) {
    summary = summary ? `${summary} - ${classLabel}` : classLabel;
  }

  if (hasTrains && trainLabel) {
    summary = summary ? `${summary} | ${trainLabel}` : trainLabel;
  }

  return summary;
};

export const formatAirlineWithFlightClass = (
  airlineName?: string,
  airlineCode?: string,
  flightClass?: string
): string => {
  const airlinePart =
    airlineName && airlineCode
      ? `${airlineName} (${airlineCode})`
      : airlineName || airlineCode || '';

  if (!flightClass) {
    return airlinePart;
  }

  return airlinePart ? `${airlinePart} - ${flightClass}` : flightClass;
};

// The included-flight baseline must NOT depend on the selected flight's
// airport: it is always the cheapest AIR item in the cabin, so the cabin
// price stays stable when the user changes flight selection.
export const getFlightIncludedItem = (selectedCabinItems: Item[] | undefined) => {
  const airItems = selectedCabinItems?.filter((item) => item?.typeCode === 'AIR');
  if (!airItems || airItems.length === 0) {
    return undefined;
  }
  if (airItems.length === 1) {
    return airItems[0];
  }
  return airItems.reduce((cheapest, current) => {
    const cheapestPrice = Number.parseFloat(cheapest?.grossPrice ?? '');
    const currentPrice = Number.parseFloat(current?.grossPrice ?? '');
    const cheapestVal = Number.isFinite(cheapestPrice) ? cheapestPrice : 0;
    const currentVal = Number.isFinite(currentPrice) ? currentPrice : 0;
    return currentVal < cheapestVal ? current : cheapest;
  });
};

export const calcPriceDifferenceAmount = (
  flight: Airfare,
  firstCabinSelections: CabinSelectionsType
) => {
  const selectedCabinItems =
    firstCabinSelections.selectedCabinPreferenceObj?.cabinPreference === CabinPreferenceEnum.REAL
      ? firstCabinSelections?.selectedCategory?.items
      : firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.items;

  const initialFlightPrice = getFlightIncludedItem(selectedCabinItems)?.grossPrice;

  if (initialFlightPrice) {
    const flightPrice = flight?.price?.total;

    return Number.parseFloat(flightPrice) - Number.parseFloat(initialFlightPrice);
  }
  return 0;
};

export const calcPriceDifference = (flight: Airfare, firstCabinSelections: CabinSelectionsType) => {
  const differenceAmount = calcPriceDifferenceAmount(flight, firstCabinSelections);

  if (differenceAmount !== 0) {
    return `${differenceAmount > 0 ? '+ ' : '- '}${getFormatPriceCurrencyOrder(
      formatPrice(roundPrice(Math.abs(differenceAmount).toString()))
    )}`;
  }
  return '0';
};
